import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { addToMainInventory } from "@/integrations/supabase/inventory-services";
import { SparePartType } from "@/types/inventory";

interface AddToMainInventoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  sparePartTypes: SparePartType[];
  onSuccess: () => void;
}

const AddToMainInventoryModal = ({ isOpen, onClose, sparePartTypes, onSuccess }: AddToMainInventoryModalProps) => {
  const [sparePartTypeId, setSparePartTypeId] = useState("");
  const [quantity, setQuantity] = useState("");
  const { toast } = useToast();

  const addMutation = useMutation({
    mutationFn: ({ sparePartTypeId, quantity }: { sparePartTypeId: string; quantity: number }) =>
      addToMainInventory(sparePartTypeId, quantity),
    onSuccess: () => {
      toast({
        title: "¡Éxito!",
        description: "Repuesto agregado al inventario principal.",
      });
      onSuccess();
      handleClose();
    },
    onError: (error) => {
      console.error("Error adding to main inventory:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Error al agregar el repuesto al inventario.",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!sparePartTypeId || !quantity) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Por favor completa todos los campos.",
      });
      return;
    }

    addMutation.mutate({
      sparePartTypeId,
      quantity: parseInt(quantity)
    });
  };

  const handleClose = () => {
    setSparePartTypeId("");
    setQuantity("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Agregar al Inventario Principal</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="sparePartType">Tipo de Repuesto</Label>
            <Select value={sparePartTypeId} onValueChange={setSparePartTypeId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecciona un tipo de repuesto" />
              </SelectTrigger>
              <SelectContent>
                {sparePartTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="quantity">Cantidad</Label>
            <Input
              id="quantity"
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="Cantidad a agregar"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={addMutation.isPending}>
              {addMutation.isPending ? "Agregando..." : "Agregar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddToMainInventoryModal;
